/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AppContextProviderWithoutMoneyAwareness,
  Props as AppContextProviderWithoutMoneyAwarenessProps,
  CreateMoneyFilterOptions,
  InputFilterStrict,
} from '@tock/coretypes';
import { isDecimalCurrency } from './money';
import { round, split, toNumber } from 'es-toolkit/compat';

export interface MoneyFilter {
  formatModelValue: (modelValue: string | number) => string;
  parseViewValue: (viewValue: string) => number | string;
  formatOnChange: boolean;
}

export function createMoneyFilter(
  decimalPlaces: number,
  allowNegative?: boolean,
  allowEmpty?: boolean
) {
  return {
    formatModelValue(modelValue: string | number): string {
      let viewValue: string | number | any = decimalPlaces === 0 && !allowEmpty ? 0 : null;

      if (typeof modelValue === 'number') {
        viewValue = (modelValue / 100.0).toFixed(decimalPlaces);
        if (toNumber(viewValue) !== modelValue / 100.0) {
          viewValue = modelValue / 100.0;
        }
        if (modelValue % 100 === 0) {
          viewValue = split(viewValue, '.')[0];
        }
        if (!allowNegative && viewValue < 0) {
          viewValue = 0;
        }
      } else if (typeof modelValue === 'string') {
        viewValue = modelValue;
      }

      return viewValue || '';
    },

    parseViewValue(viewValue: string) {
      let modelValue = allowEmpty ? undefined : 0;

      if (viewValue) {
        const parsedValue = parseFloat(viewValue);

        if (Number.isFinite(parsedValue)) {
          modelValue = round(parsedValue * 100.0, 0);

          if (!allowNegative && modelValue < 0) {
            modelValue = 0;
          }
        }
      }

      return (typeof modelValue === 'number') ? modelValue : modelValue || '';
    },

    formatOnChange: decimalPlaces === 0,
  };
}

/** Like the original, except this only accepts and returns numbers */
export const createMoneyFilterStrict = (
  decimalPlaces: 0 | 2 = 0, // doesn't work with 1 or anything >2 because we’re assuming cents<>dollar conversion
  allowNegative?: boolean
): InputFilterStrict<number> => ({
  format(modelValue: number): string {
    let viewValue = (modelValue / 100.0).toFixed(decimalPlaces);

    if (toNumber(viewValue) !== modelValue / 100.0) {
      viewValue = String(modelValue / 100.0);
    }
    if (modelValue % 100 === 0) {
      viewValue = viewValue.split('.')[0];
    }
    if (!allowNegative && modelValue < 0) {
      viewValue = '0';
    }

    return viewValue;
  },

  parse(viewValue: string): number {
    let modelValue = 0;

    if (viewValue) {
      const parsedValue = parseFloat(viewValue);

      if (Number.isFinite(parsedValue)) {
        let centsValue = round(parsedValue * 100.0, 0);

        if (!allowNegative && centsValue < 0) {
          centsValue = 0;
        }

        modelValue = centsValue;
      }
    }

    return modelValue;
  },

  formatOnChange: decimalPlaces === 0,
});

export const strictDecimalsMoneyFilter = createMoneyFilterStrict(2);

/**
 * This returns a creator function that will return a pre-configured money filter
 * that takes into account if a currency has decimal places or not. You still request
 * 2 or 0 for decimal places, but if it does not have decimals, then it will set it to
 * zero fo you.
 * @param {string} currency
 */
const createMoneyFilterCreator = (currency: string) => {
  const hasDecimals = isDecimalCurrency(currency);
  const realDecimalScale = hasDecimals ? 2 : 0;
  const filterWithDecimals = createMoneyFilterStrict(realDecimalScale);
  const filter = createMoneyFilterStrict(0);
  const filterWithDecimalsNegative = createMoneyFilterStrict(realDecimalScale, true);
  const filterNegative = createMoneyFilterStrict(0, true);

  return function createMoneyFilter(options?: CreateMoneyFilterOptions) {
    const defaultOptions: CreateMoneyFilterOptions = {
      allowNegative: false,
      decimalScale: 2,
    };
    const { allowNegative, decimalScale } = { ...defaultOptions, ...options };
    if (!allowNegative && decimalScale === 2) {
      return filterWithDecimals;
    }
    if (allowNegative && decimalScale === 2) {
      return filterWithDecimalsNegative;
    }
    if (!allowNegative && decimalScale === 0) {
      return filter;
    }
    if (allowNegative && decimalScale === 0) {
      return filterNegative;
    }
    return filterWithDecimals;
  };
};

type Props = Omit<AppContextProviderWithoutMoneyAwarenessProps, 'createMoneyFilterCreator'>;

/**
 * A component that provides common context for Tock applications. This is factored into @tock/money instead of @tock/coretypes
 * to avoid circular dependencies between the two packages.
 */
export const AppContextProvider = (props: React.PropsWithChildren<Props>) => (
  <AppContextProviderWithoutMoneyAwareness
    {...props}
    createMoneyFilterCreator={createMoneyFilterCreator}
  />
);
